/**
 * Every rule about which sports and leagues the onboarding steps show and
 * which arrive selected, with no renderer and no fetch.
 */

import type { typeCatalogSportInput } from "../scores/leagueSelectorRowModel";

export type typeOnboardingSportOption = {
  sportId: string;
  sportKey: string;
  name: string;
};

export type typeOnboardingLeagueOption = {
  leagueId: string;
  leagueKey: string;
  shortName: string;
  isComingSoon: boolean;
};

export type typeOnboardingLeagueGroup = {
  sportId: string;
  heading: string;
  leagues: typeOnboardingLeagueOption[];
  /** False when every league in the group is coming soon. The frame drops
   *  the helper line under a group with nothing to pick. */
  doShowGroupHelp: boolean;
};

export function isRenderableLeague(input: {
  visibilityState: string;
}): boolean {
  return input.visibilityState !== "hidden";
}

export function buildSportOptions(input: {
  sports: typeCatalogSportInput[];
}): typeOnboardingSportOption[] {
  const options: typeOnboardingSportOption[] = [];
  for (const sport of input.sports) {
    const hasRenderableLeague = sport.leagues.some((league) =>
      isRenderableLeague({ visibilityState: league.visibilityState }),
    );
    if (!hasRenderableLeague) continue;
    options.push({
      sportId: sport.sportId,
      sportKey: sport.sportKey,
      name: sport.name,
    });
  }
  return options;
}

export function buildLeagueGroups(input: {
  sports: typeCatalogSportInput[];
  selectedSportIds: string[];
}): typeOnboardingLeagueGroup[] {
  const keepAllSports = input.selectedSportIds.length === 0;
  const groups: typeOnboardingLeagueGroup[] = [];
  for (const sport of input.sports) {
    if (!keepAllSports && !input.selectedSportIds.includes(sport.sportId))
      continue;
    const leagues: typeOnboardingLeagueOption[] = [];
    for (const league of sport.leagues) {
      if (!isRenderableLeague({ visibilityState: league.visibilityState }))
        continue;
      leagues.push({
        leagueId: league.leagueId,
        leagueKey: league.leagueKey,
        shortName: league.shortName,
        isComingSoon: league.visibilityState === "comingSoon",
      });
    }
    if (leagues.length === 0) continue;
    groups.push({
      sportId: sport.sportId,
      heading: sport.name,
      leagues,
      doShowGroupHelp: leagues.some((league) => !league.isComingSoon),
    });
  }
  return groups;
}

export function resolveInitialLeagueIds(input: {
  sports: typeCatalogSportInput[];
  selectedSportIds: string[];
  followedLeagueIds: string[];
}): string[] {
  const selectableLeagueIds = new Set<string>();
  for (const sport of input.sports) {
    for (const league of sport.leagues) {
      if (
        isRenderableLeague({ visibilityState: league.visibilityState }) &&
        league.visibilityState !== "comingSoon"
      ) {
        selectableLeagueIds.add(league.leagueId);
      }
    }
  }

  const result: string[] = [];
  const seen = new Set<string>();

  for (const leagueId of input.followedLeagueIds) {
    if (!selectableLeagueIds.has(leagueId)) continue;
    if (seen.has(leagueId)) continue;
    seen.add(leagueId);
    result.push(leagueId);
  }

  for (const sport of input.sports) {
    if (!input.selectedSportIds.includes(sport.sportId)) continue;
    for (const league of sport.leagues) {
      if (!selectableLeagueIds.has(league.leagueId)) continue;
      if (seen.has(league.leagueId)) continue;
      seen.add(league.leagueId);
      result.push(league.leagueId);
    }
  }

  return result;
}

export function toggleSportId(input: {
  selectedSportIds: string[];
  sportId: string;
}): string[] {
  if (input.selectedSportIds.includes(input.sportId)) {
    return input.selectedSportIds.filter(
      (sportId) => sportId !== input.sportId,
    );
  }
  return [...input.selectedSportIds, input.sportId];
}
